import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import NewsTicker from "./NewsTicker";
import ThemeToggle from "./ThemeToggle";
import { LOGO_NAME_GREEN, FAVICON } from "../constants";
import { applyNoOrphans } from "../utils/noOrphans";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const NAV_LINKS = [
  { to: "/", label: "Home", dropdown: [
    { label: "Try It", hash: "#try-it-now" },
    { label: "Features", hash: "#features" },
  ]},
  { to: "/introducing", label: "About" },
  { to: "/install", label: "Install" },
  { to: "/pricing", label: "Pricing" },
  { to: "/contact", label: "Contact" },
];

const Layout = ({ children, showNav = true }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' ? window.innerWidth >= 768 : true);
  // Wide-desktop = enough horizontal room to safely show the in-nav news ticker
  // alongside the logo, full link cluster, theme toggle, and Sign In button without
  // any overlap or crowding. Below this, the ticker is hidden entirely (the
  // bottom-position ticker variant still works for users who prefer it).
  const [isWideDesktop, setIsWideDesktop] = useState(typeof window !== 'undefined' ? window.innerWidth >= 1280 : true);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [scrollY, setScrollY] = useState(0);
  const [headlines, setHeadlines] = useState([]);
  const [showFavicons, setShowFavicons] = useState(true);
  const [appVersion, setAppVersion] = useState(null);
  const [tickerPosition, setTickerPosition] = useState(
    typeof window !== "undefined" ? localStorage.getItem("ts_ticker_position") || "top" : "top"
  );
  const dropdownRef = useRef(null);

  // Listen for ticker position changes from NewsPreferencesPage
  useEffect(() => {
    const onChange = (e) => setTickerPosition(e.detail || "top");
    window.addEventListener("ts:ticker-position-change", onChange);
    return () => window.removeEventListener("ts:ticker-position-change", onChange);
  }, []);

  // Fetch the deployed version once on mount, surface it in the footer so we can
  // verify at a glance which build is actually serving each region. Fails silently
  // if the backend is asleep (preview env) — the footer just hides the chip.
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    if (!API) return;
    let cancelled = false;
    fetch(`${API}/api/version`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!cancelled && data?.version) setAppVersion(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // No-orphans fallback for browsers without `text-wrap: pretty` support.
  // Re-runs after each route change since React swaps in new children content.
  useEffect(() => {
    // Defer to next paint so React has rendered the new route's content.
    const id = window.requestAnimationFrame(() => applyNoOrphans(document));
    return () => window.cancelAnimationFrame(id);
  }, [location.pathname]);

  // Track scroll for frosted-glass navbar
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Fetch news headlines for ticker (auth-aware so user prefs apply when logged in)
  useEffect(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get(`${API}/news/headlines`, { withCredentials: true, headers }).then(res => {
      if (res.data?.headlines?.length) setHeadlines(res.data.headlines);
      if (typeof res.data?.show_favicons === 'boolean') setShowFavicons(res.data.show_favicons);
    }).catch(() => {});
  }, [user?.user_id]);

  // Scroll-triggered gradient color shift on body
  useEffect(() => {
    const maxScroll = 2000;
    const t = Math.min(scrollY / maxScroll, 1);
    // Interpolate: blue-dominant → teal-dominant
    const lerp = (a, b) => Math.round(a + (b - a) * t);
    const hex = (r, g, b) => `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
    const c1 = hex(lerp(25,6), lerp(6,171), lerp(167,193));
    const c2 = hex(lerp(7,8), lerp(7,145), lerp(171,178));
    const c3 = hex(lerp(8,20), lerp(209,184), lerp(209,166));
    const c4 = hex(lerp(6,45), lerp(171,212), lerp(193,191));
    const c5 = hex(lerp(6,94), lerp(171,234), lerp(193,212));
    document.body.style.background = `linear-gradient(-60deg, ${c1} 0%, ${c2} 5%, ${c3} 47%, ${c4} 89%, ${c5} 92%)`;
    document.body.style.backgroundSize = '200% 200%';
    document.body.style.animation = 'ani_gradient 18.2s ease infinite';
  }, [scrollY]);

  useEffect(() => {
    const onResize = () => {
      setIsDesktop(window.innerWidth >= 768);
      setIsWideDesktop(window.innerWidth >= 1280);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDropdownNav = (to, hash) => {
    setOpenDropdown(null);
    setMobileOpen(false);
    if (location.pathname === to) {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(to);
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  };

  const isActive = (path) => location.pathname === path;

  // Frosted-glass navbar interpolation based on scroll
  const scrollProgress = Math.min(scrollY / 120, 1);
  const navBg = `rgba(6, 7, 43, ${0.15 + scrollProgress * 0.55})`;
  const navBlur = 8 + scrollProgress * 24;
  const navBorder = `rgba(255, 255, 255, ${0.05 + scrollProgress * 0.1})`;
  const navShadow = scrollProgress > 0.1
    ? `0 4px 30px rgba(25, 6, 167, ${scrollProgress * 0.15}), 0 1px 8px rgba(8, 209, 209, ${scrollProgress * 0.08})`
    : 'none';

  return (
    <div className="min-h-screen animated-smoke-bg">
      {showNav && (
        <nav
          className="fixed top-0 left-0 right-0 z-50"
          style={{
            background: navBg,
            backdropFilter: `blur(${navBlur}px) saturate(${1.2 + scrollProgress * 0.6})`,
            WebkitBackdropFilter: `blur(${navBlur}px) saturate(${1.2 + scrollProgress * 0.6})`,
            borderBottom: `1px solid ${navBorder}`,
            boxShadow: navShadow,
            transition: 'background 0.35s ease, box-shadow 0.35s ease, border-bottom 0.35s ease',
          }}
          data-testid="navbar"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Equal-spacing navbar — single flex row, `gap-6` enforces a uniform 24px
                channel between EVERY item. The News Ticker is `flex-1` so it absorbs
                whatever space is left after the fixed-width items measure themselves;
                it grows on wide screens, shrinks gracefully toward 0 on narrow ones,
                never crowds its neighbours because flexbox keeps the gap intact. */}
            <div className="flex items-center gap-6 h-16">
              <Link to="/" className="flex items-center shrink-0" data-testid="nav-logo" onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                <img
                  src={LOGO_NAME_GREEN}
                  alt="Top Screen"
                  className="w-auto"
                  style={{ height: "70px" }}
                />
              </Link>

              {/* News Ticker — grows/shrinks with viewport. min-w-0 + basis-0 makes
                  flexbox the sole arbiter of its width. */}
              {isDesktop && headlines.length > 0 && tickerPosition === "top" && (
                <div className="flex-1 min-w-0 basis-0" data-testid="nav-ticker-slot">
                  <NewsTicker headlines={headlines} variant="navbar" />
                </div>
              )}

              {/* Desktop nav cluster — every link, the theme toggle, and Sign In are
                  flat siblings of the parent flex row, so the same `gap-6` applies
                  uniformly between each of them. */}
              {isDesktop && NAV_LINKS.map((link) => (
                link.dropdown ? (
                  <div key={link.to} className="relative shrink-0" ref={dropdownRef}>
                    <button
                      className={`font-medium transition-colors flex items-center gap-1 ${isActive(link.to) ? "text-[#A3E635]" : "text-white/80 hover:text-white"}`}
                      data-testid={`nav-${link.label.toLowerCase()}`}
                      onClick={() => {
                        navigate(link.to);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        setOpenDropdown(openDropdown === link.label ? null : link.label);
                      }}
                    >
                      {link.label}
                      <svg className={`w-3.5 h-3.5 transition-transform ${openDropdown === link.label ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M19 9l-7 7-7-7"/></svg>
                    </button>
                    {openDropdown === link.label && (
                      <div className="absolute top-full left-0 mt-2 w-28 rounded-lg border border-white/10 overflow-hidden" style={{ background: "rgba(0,0,0,0.95)", backdropFilter: "blur(20px)" }} data-testid="nav-home-dropdown">
                        {link.dropdown.map((sub) => (
                          <button
                            key={sub.hash}
                            onClick={() => handleDropdownNav(link.to, sub.hash)}
                            className="w-full text-left px-4 py-2.5 text-sm text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                            data-testid={`nav-dropdown-${sub.hash.replace('#', '')}`}
                          >
                            {sub.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`font-medium transition-colors shrink-0 whitespace-nowrap ${isActive(link.to) ? "text-[#A3E635]" : "text-white/80 hover:text-white"}`}
                    data-testid={`nav-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Link>
                )
              ))}

              {/* Theme toggle — between Contact and Sign In (still a direct sibling
                  of the parent flex so gap-6 applies on both sides). */}
              {isDesktop && <ThemeToggle />}

              {isDesktop && (
                user ? (
                  <>
                    <Link to="/dashboard" className={`font-medium transition-colors shrink-0 whitespace-nowrap ${isActive("/dashboard") ? "text-[#A3E635]" : "text-white/80 hover:text-white"}`} data-testid="nav-dashboard">Dashboard</Link>
                    <Link to="/pip-dashboard" className={`font-medium transition-colors shrink-0 whitespace-nowrap ${isActive("/pip-dashboard") ? "text-[#A3E635]" : "text-white/80 hover:text-white"}`} data-testid="nav-pip-dashboard">PiP Manager</Link>
                    <button onClick={() => { logout(); navigate("/"); }} className="font-medium text-white/80 hover:text-white transition-colors shrink-0 whitespace-nowrap" data-testid="nav-logout">Logout</button>
                  </>
                ) : (
                  <Link to="/login" className="bg-[#A3E635] text-black px-6 py-2 rounded-full font-semibold hover:bg-[#8CD025] transition-all shrink-0 whitespace-nowrap" data-testid="nav-login">{'Sign\u00A0In'}</Link>
                )
              )}

              {/* Mobile hamburger — pushed to the right edge by the flex-1 ticker (or
                  by an empty spacer when no headlines). */}
              {!isDesktop && headlines.length === 0 && <div className="flex-1" />}
              {!isDesktop && (
                <button
                  className="md:hidden flex flex-col gap-1.5 p-2 shrink-0"
                  onClick={() => setMobileOpen(!mobileOpen)}
                  data-testid="nav-mobile-toggle"
                  aria-label="Toggle menu"
                >
                  <span className={`block w-5 h-0.5 bg-white transition-transform ${mobileOpen ? "rotate-45 translate-y-2" : ""}`} />
                  <span className={`block w-5 h-0.5 bg-white transition-opacity ${mobileOpen ? "opacity-0" : ""}`} />
                  <span className={`block w-5 h-0.5 bg-white transition-transform ${mobileOpen ? "-rotate-45 -translate-y-2" : ""}`} />
                </button>
              )}
            </div>
          </div>

          {/* Mobile menu */}
          {mobileOpen && !isDesktop && (
            <div className="md:hidden border-t border-white/10" style={{ background: "rgba(0,0,0,0.95)" }} data-testid="nav-mobile-menu">
              <div className="px-4 py-4 space-y-1">
                {NAV_LINKS.map((link) => (
                  <div key={link.to}>
                    <Link
                      to={link.to}
                      onClick={() => { setMobileOpen(false); setOpenDropdown(null); }}
                      className={`block px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive(link.to) ? "text-[#A3E635] bg-[#A3E635]/10" : "text-[#E8E4DC] hover:text-white hover:bg-white/5"}`}
                      data-testid={`nav-mobile-${link.label.toLowerCase()}`}
                    >
                      {link.label}
                    </Link>
                    {link.dropdown && (
                      <div className="pl-6 space-y-1 mt-1">
                        {link.dropdown.map((sub) => (
                          <button
                            key={sub.hash}
                            onClick={() => handleDropdownNav(link.to, sub.hash)}
                            className="block w-full text-left px-3 py-2 rounded-lg text-sm text-[#E8E4DC] hover:text-white hover:bg-white/5 transition-colors"
                            data-testid={`nav-mobile-dropdown-${sub.hash.replace('#', '')}`}
                          >
                            {sub.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {user ? (
                  <>
                    <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="block px-3 py-2.5 rounded-lg text-sm font-medium text-[#E8E4DC] hover:text-white hover:bg-white/5" data-testid="nav-mobile-dashboard">Dashboard</Link>
                    <Link to="/pip-dashboard" onClick={() => setMobileOpen(false)} className="block px-3 py-2.5 rounded-lg text-sm font-medium text-[#E8E4DC] hover:text-white hover:bg-white/5" data-testid="nav-mobile-pip-dashboard">PiP Manager</Link>
                    <button onClick={() => { logout(); navigate("/"); setMobileOpen(false); }} className="block w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium text-[#E8E4DC] hover:text-white hover:bg-white/5" data-testid="nav-mobile-logout">Logout</button>
                  </>
                ) : (
                  <Link to="/login" onClick={() => setMobileOpen(false)} className="block mx-3 mt-2 text-center bg-[#A3E635] text-black px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#8CD025] transition-all" data-testid="nav-mobile-login">Sign In</Link>
                )}
              </div>
            </div>
          )}

          {/* Animated ocean wave border */}
          <div className="nav-wave-container">
            <svg className="nav-wave nav-wave-back" viewBox="0 0 1200 14" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 8 Q75 0 150 8 T300 8 T450 8 T600 8 T750 8 T900 8 T1050 8 T1200 8 V14 H0Z" fill="rgba(8,209,209,0.4)"/>
            </svg>
            <svg className="nav-wave nav-wave-mid" viewBox="0 0 1200 14" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 6 Q60 12 120 6 T240 6 T360 6 T480 6 T600 6 T720 6 T840 6 T960 6 T1080 6 T1200 6 V14 H0Z" fill="rgba(6,171,193,0.5)"/>
            </svg>
            <svg className="nav-wave nav-wave-front" viewBox="0 0 1200 14" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 10 Q50 4 100 10 T200 10 T300 10 T400 10 T500 10 T600 10 T700 10 T800 10 T900 10 T1000 10 T1100 10 T1200 10 V14 H0Z" fill="rgba(25,6,167,0.45)"/>
            </svg>
          </div>
        </nav>
      )}

      <main className={showNav ? "pt-20" : ""} style={tickerPosition === "bottom" && headlines.length > 0 ? { paddingBottom: "44px" } : undefined}>{children}</main>

      {/* Fixed bottom news ticker (when user preference = bottom) */}
      {isDesktop && headlines.length > 0 && tickerPosition === "bottom" && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 h-11 flex items-center border-t border-white/10"
          style={{ background: "rgba(0,0,0,0.92)", backdropFilter: "blur(12px)" }}
          data-testid="news-ticker-bottom"
        >
          <div className="flex-1 px-12 h-full flex items-center">
            <NewsTicker headlines={headlines} variant="footer" />
          </div>
        </div>
      )}

      <footer className="border-t border-white/10 bg-black/80 py-8" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Stack on mobile, row on md+. `flex-wrap` + min-w-0 prevent any element
              from overflowing or crowding its neighbour at narrow widths. */}
          <div className="flex flex-col md:flex-row md:flex-wrap justify-between items-center gap-x-6 gap-y-3">
            <div className="flex items-center gap-3 min-w-0 text-center md:text-left">
              <img src={FAVICON} alt="Top Screen" className="h-12 w-12 shrink-0" />
              <span className="text-[#E8E4DC] text-sm break-words">
                &copy; 2026 All Rights Reserved | 713 C &amp; D | Keith Bolden, LTJ
                {appVersion?.version && (
                  <span
                    className="ml-2 inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-white/5 border border-white/10 text-[#A3E635]/80 align-middle"
                    title={appVersion.build ? `Build ${appVersion.build}` : `Top Screen v${appVersion.version}`}
                    data-testid="footer-version"
                  >
                    v{appVersion.version}{appVersion.build ? ` · ${appVersion.build}` : ''}
                  </span>
                )}
              </span>
            </div>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 shrink-0">
              <Link to="/terms" className="text-[#E8E4DC] hover:text-[#E8E4DC] text-sm transition-colors whitespace-nowrap" data-testid="footer-terms">Terms of Service</Link>
              <Link to="/privacy" className="text-[#E8E4DC] hover:text-[#E8E4DC] text-sm transition-colors whitespace-nowrap" data-testid="footer-privacy">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
